import {
  coachCurrentRole,
  coachDisplayName,
} from '@efdigital/ll-addon-coaching';
import CoachCardAvatar from '@efdigital/ll-addon-coaching/components/coach-card/avatar';
import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import { getSessionStatusDisplay } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-display';
import type {
  CoachingConfig,
  CoachingSession,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import type { TOC } from '@ember/component/template-only';

import SessionNotes from '../session-notes.gts';
import { SessionActions } from './session-actions.gts';

interface SessionCardSignature {
  Args: {
    session: CoachingSession;
    coach: CoachProfile;
    coachingConfig: CoachingConfig;
    planner: CoachingPlanner;
    onChanged?: () => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

function isPast(session: CoachingSession): boolean {
  return new Date(session.startTime) < new Date();
}

function toDate(date: string): Date {
  return new Date(date);
}

/**
 * Danger (canceled) uses explicit white type: `text-bg-danger` picks dark
 * text against this theme's coral fill.
 */
function statusBadgeClass(color: string): string {
  if (color === 'danger') {
    return 'bg-danger text-white';
  }

  return `text-bg-${color}`;
}

const SessionCard: TOC<SessionCardSignature> = <template>
  <div class="card border shadow-sm rounded-4 overflow-hidden" ...attributes>
    <div class="card-body p-3 px-md-4 py-md-3">
      {{! The avatar sits beside the session header only. Notes render as a sibling
          below, so they get the full card width instead of being squeezed into the
          column left over next to the avatar on narrow screens. }}
      <div class="d-flex flex-row align-items-start gap-3">
        <CoachCardAvatar
          @coach={{@coach}}
          @size="sm"
          @avatarUrl={{@coach.photo}}
          class="{{if (isPast @session) 'text-secondary' 'text-primary'}}"
        />

        <div class="flex-grow-1 min-w-0 d-flex flex-column gap-3">
          <div
            class="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3"
          >
            <div class="min-w-0">
              <h3
                class="h5 fw-bold mb-1 text-capitalize"
              >{{@session.sessionType}}</h3>
              <p class="text-body-secondary mb-2">
                {{coachDisplayName @coach}}
                ·
                {{coachCurrentRole @coach}}
              </p>
              <div
                class="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 small text-body-secondary fw-semibold"
              >
                <span>{{formatDate (toDate @session.startTime)}}</span>
                <span>{{formatTimeRange
                    (toDate @session.startTime)
                    (toDate @session.endTime)
                  }}</span>
                {{#let
                  (getSessionStatusDisplay @session.status)
                  as |statusDisplay|
                }}
                  <span
                    class="badge rounded-pill
                      {{statusBadgeClass statusDisplay.color}}
                      fw-semibold px-2 py-1 d-flex align-items-center gap-1"
                    title={{statusDisplay.description}}
                    data-test-session-status-badge
                  >
                    <iconify-icon
                      inline
                      icon={{statusDisplay.icon}}
                      aria-hidden="true"
                    />
                    {{statusDisplay.label}}
                    <span class="visually-hidden">:
                      {{statusDisplay.description}}</span>
                  </span>
                {{/let}}
              </div>
            </div>

            <div
              class="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap"
            >
              <SessionActions
                @session={{@session}}
                @coach={{@coach}}
                @coachingConfig={{@coachingConfig}}
                @planner={{@planner}}
                @buttonClass="btn-sm"
                @onChanged={{@onChanged}}
              />
            </div>
          </div>
        </div>
      </div>

      {{! `session.notes` is the note this coachee left when booking, which is
          embedded in the calendar invite - labelled distinctly so it is not
          confused with the private note below. }}
      {{#if @session.notes}}
        <div class="rounded-3 p-3 bg-body mt-3">
          <p class="mb-0 small text-body">
            <span class="fw-bold">Your booking note</span>
            ·
            {{@session.notes}}
          </p>
        </div>
      {{/if}}

      <SessionNotes @session={{@session}} @role="coachee" class="mt-3" />
    </div>
  </div>
</template>;

export default SessionCard;
